import { LoaderIcon } from '@/app/features/icons/Loader'
import { faClose, FaIcon } from '@/lib/fa'
import ModalLayout from '@/modules/drivers/modals/shared/ModalLayout'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import {
  useLoadingStore,
  selectLoading,
} from '@/modules/shared/stores/loadingStore'
import React, { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import AttestationTable from './components/AttestationTable'
import type { AttestationRow } from './components/AttestationRowItem'
import { TableProvider, useTable } from '@/modules/drivers/context/TableContext'
import { TooltipBoundaryContext } from '@/modules/drivers/context/TooltipBoundaryContext'
import AttestationsService from '@/modules/drivers/services/AttestationsService'
import AttestationsActions from './components/AttestationsActions'
import { useAttestationsDownload } from './components/useAttestationsDownload'

// Pasek akcji jest renderowany poza przewijaną tabelą - inaczej jego tooltipy chowają
// się pod sticky nagłówkiem i są przycinane przez kontener z overflow.
const AttestationsModalActions = ({
  companyId,
  onAfterDelete,
}: {
  companyId: string
  onAfterDelete: () => void
}) => {
  const { isSelectedAny, selectedGroup, resetAll } = useTable()

  if (!isSelectedAny) return null

  // z-index nad sticky nagłówkiem tabeli, inaczej tooltipy chowają się pod nim
  return (
    <div className="tw-relative tw-z-[60]">
      <AttestationsActions
        companyId={companyId}
        attestationIds={selectedGroup?.ids ?? []}
        onAfterDelete={() => {
          resetAll()
          onAfterDelete()
        }}
      />
    </div>
  )
}

// Przycisk "Potwierdź" pobiera zaznaczone zaświadczenia i zamyka modal - musi więc
// widzieć zaznaczenie z TableContext, stąd osobny komponent wewnątrz TableProvider.
const AttestationsModalFooter = ({
  companyId,
  handleClose,
}: {
  companyId: string
  handleClose: () => void
}) => {
  const { t } = useLocalize()
  const { selectedGroup } = useTable()
  const { downloadSelected } = useAttestationsDownload(companyId)

  const handleConfirm = async () => {
    await downloadSelected(selectedGroup?.ids ?? [])
    handleClose()
  }

  return (
    <div className="tw-flex tw-justify-end tw-border-t tw-border-base-300">
      <button
        type="button"
        title={t('operation.confirm')}
        onClick={handleConfirm}
        className={`tw-m-4 tw-cursor-pointer tw-rounded-lg tw-bg-primary tw-p-3 tw-text-sm tw-font-medium tw-text-white hover:tw-opacity-80`}
      >
        {t('operation.confirm')}
      </button>
    </div>
  )
}

const AttestationsModal = ({ handleClose, driverId, companyId }) => {
  const [loading, setLoading] = React.useState(false)
  const globalLoading = useLoadingStore(selectLoading)
  const [attestations, setAttestations] = React.useState<AttestationRow[]>([])
  const modalRef = React.useRef<HTMLDivElement>(null)
  const { getAttestations } = AttestationsService()


  // Modal jest renderowany poza zagnieżdżonymi <Routes>, więc useParams() nie widzi
  // parametru :year - rok czytamy wprost ze ścieżki wybranego widoku aktywności.
  const { pathname } = useLocation()
  const yearFromPath = pathname.match(
    /(?:yearly|monthly|weekly|daily)Activity\/(\d{4})/i,
  )?.[1]
  const year = yearFromPath ?? String(new Date().getFullYear())

  const fetchAttestations = async () => {
    setLoading(true)
    try {
      const res = await getAttestations({
        companyId,
        driverId,
        year: Number(year),
      })

      setAttestations(res)
    } catch (e) {
      console.error('Error fetching attestations', e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAttestations()
  }, [year, driverId, companyId])

  return (
    <ModalLayout customZIndex={9999}>
      <div
        ref={modalRef}
        className="tw-relative tw-flex tw-h-fit tw-max-h-[90vh] tw-w-full tw-max-w-[90vw] tw-flex-col tw-rounded-lg tw-bg-white tw-shadow-2xl"
      >
        {(loading || globalLoading) && (
          <div className="tw-absolute tw-inset-0 tw-z-[200] tw-grid tw-place-items-center tw-rounded-lg tw-bg-black/30 tw-backdrop-blur-sm">
            <LoaderIcon className="tw-animate-spin" />
          </div>
        )}
        <div className="tw-flex tw-items-center tw-justify-between tw-border-b tw-border-base-300 tw-p-4">
          <div className="tw-flex tw-items-center tw-gap-4">
            <p className="tw-m-0 tw-text-xl tw-font-semibold tw-text-content-base">
              Lista zaświadczeń
            </p>
          </div>
          <FaIcon
            icon={faClose}
            className="tw-mr-4 tw-h-5 tw-w-5 tw-cursor-pointer tw-text-content-base hover:tw-text-primary"
            onClick={() => handleClose()}
          />
        </div>
        <TableProvider bulk>
          {/* Tooltipy paska akcji trzymamy w granicach modalu, żeby nie wychodziły poza okno */}
          <TooltipBoundaryContext.Provider value={modalRef}>
            <AttestationsModalActions
              companyId={companyId}
              onAfterDelete={handleClose}
            />
          </TooltipBoundaryContext.Provider>
          <div className={`tw-overflow-auto`}>
            <AttestationTable attestations={attestations} />
          </div>
          <AttestationsModalFooter
            companyId={companyId}
            handleClose={handleClose}
          />
        </TableProvider>
      </div>
    </ModalLayout>
  )
}

export default AttestationsModal
